export interface SelfRegistrationVisibilityInput {
  selfRegistrationEnabled: boolean;
  browserUrl?: string | null;
  configuredProgramUrl?: string | null;
}

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

function canonicalizeHost(hostname: string): string {
  const lowered = hostname.trim().toLowerCase();
  if (LOOPBACK_HOSTS.has(lowered)) {
    return 'localhost';
  }

  return lowered;
}

function normalizedPort(protocol: string, port: string): string {
  const loweredProtocol = protocol.toLowerCase();
  const rawPort = port.trim();

  if (!rawPort) {
    return '';
  }

  if ((loweredProtocol === 'http:' && rawPort === '80') || (loweredProtocol === 'https:' && rawPort === '443')) {
    return '';
  }

  return rawPort;
}

function normalizedPath(pathname: string): string {
  if (!pathname) {
    return '/';
  }

  const trimmed = pathname.trim().replace(/\/+$/, '');
  return trimmed.length === 0 ? '/' : trimmed;
}

export function normalizeProgramAwareUrl(input: string): string | null {
  const raw = input?.trim();
  if (!raw) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }

  const protocol = parsed.protocol.toLowerCase();
  if (protocol !== 'http:' && protocol !== 'https:') {
    return null;
  }

  const host = canonicalizeHost(parsed.hostname);
  const port = normalizedPort(protocol, parsed.port);
  const path = normalizedPath(parsed.pathname);

  return `${protocol}//${host}${port ? `:${port}` : ''}${path}`;
}

export function isProgramAwareUrlMatch(browserUrl?: string | null, configuredProgramUrl?: string | null): boolean {
  if (!browserUrl || !configuredProgramUrl) {
    return false;
  }

  const normalizedBrowser = normalizeProgramAwareUrl(browserUrl);
  const normalizedProgram = normalizeProgramAwareUrl(configuredProgramUrl);

  if (!normalizedBrowser || !normalizedProgram) {
    return false;
  }

  return normalizedBrowser === normalizedProgram;
}

export function shouldShowSelfRegistrationOption(input: SelfRegistrationVisibilityInput): boolean {
  if (!input.selfRegistrationEnabled) {
    return false;
  }

  return isProgramAwareUrlMatch(input.browserUrl, input.configuredProgramUrl);
}
